import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import { ENVIRONMENT } from '@company-box/models';

export type AiDecalSide = 'INSIDE' | 'OUTSIDE';
export type AiDecalScope = 'WHOLE_BOX' | 'FACE';
export type AiDecalContentType = 'IMAGE' | 'SOLID_COLOR' | 'TEXT';

export interface AiDecalRequest {
  prompt: string;
  objectGuids?: string[];
  userId?: number;
}

export interface AiDecalElementResult {
  objectGuid: string;
  /** Short code (e.g. "a1") the user can type in a follow-up prompt to target this element. */
  reference: string;
  scope: AiDecalScope;
  face: string | null;
  contentType: AiDecalContentType;
  designPrompt?: string;
  /** The prior guid this element replaced, or undefined if it's a brand new placement. */
  refinedObjectGuid?: string;
  createdAt?: string;
  /** Non-error caveat about this placement (e.g. a recolor request the backend couldn't apply). The element still placed normally. */
  note?: string;
  /** Ready-to-display sentence describing what happened to this element, e.g. "Rotated it to -90° on Top (outside)". */
  summary: string;
}

export interface AiDecalElementFailure {
  scope?: AiDecalScope;
  face?: string;
  contentType?: AiDecalContentType;
  message: string;
}

export interface AiDecalResponse {
  containerName: string;
  side: AiDecalSide;
  elements: AiDecalElementResult[];
  failures: AiDecalElementFailure[];
}

// Marks a message as written for the chat user (backend validation text, or a plain "request
// rejected" line) - as opposed to a caught JS error's own .message, which is an implementation
// detail and unsafe to show verbatim. Callers should only display error.message when the error
// is an AiDecalError.
export class AiDecalError extends Error {}

@Injectable({
  providedIn: "root",
})
export class DesignService {
  private httpClient: HttpClient = inject(HttpClient);
  constructor() {}
  private environment = inject(ENVIRONMENT);

  getPatternCollections(): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/pattern/collection/list?published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getPatterns(collectionId: number, offset: number = 0, limit: number = 50): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/pattern/list?collection=${collectionId}&limit=${limit}&offset=${offset}&published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getShapeCollections(): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/shape/collection/list?published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getShapes(collectionId: number, offset: number = 0, limit: number = 50): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/shape/list?collection=${collectionId}&limit=${limit}&offset=${offset}&published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getDesign(userID: number, containerID: string): Promise<any> {
    // Cache-bust: a local dev gateway/proxy in front of apiHost has been observed serving stale
    // cached responses (even 304s to non-GET requests) for these design endpoints, so every read
    // must be forced fresh rather than relying on the browser/proxy cache.
    const url = `${this.environment.apiHost}/project/design/${userID}/container/${containerID}?x=${Date.now()}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  createDesign(userID: number, design: any): Promise<any> {
    const url = `${this.environment.apiHost}/project/design/${userID}/`;

    const payload = JSON.stringify(design);

    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, payload, headers).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  updateDesign(userID: number, design: any): Promise<any> {
    const url = `${this.environment.apiHost}/project/design/${userID}/`;

    const payload = JSON.stringify(design);

    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return new Promise<any>((resolve, reject) => {
      this.httpClient.put(url, payload, headers).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  deleteDesign(userId: number, designId: number): Promise<any> {
    const url = `${this.environment.apiHost}/project/design/${userId}/${designId}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.delete(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  generateAiDecal(
    containerId: string,
    prompt: string,
    priorObjectGuids?: string[],
    userId?: number
  ): Promise<AiDecalResponse> {
    const url = `${this.environment.apiHost}/project/container/${containerId}/design/decal`;
    const request: AiDecalRequest = {
      prompt,
      ...(priorObjectGuids?.length ? { objectGuids: priorObjectGuids } : {}),
      ...(userId ? { userId } : {}),
    };

    return new Promise<AiDecalResponse>((resolve, reject) => {
      this.httpClient.post<AiDecalResponse>(url, request).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            const message = this.extractAiDecalErrorMessage(error.error);
            if (message) {
              reject(new AiDecalError(message));
              return;
            }
            if (error.status >= 400 && error.status < 500) {
              reject(new AiDecalError('The request was rejected.'));
              return;
            }
          }
          reject(error);
        }
      );
    });
  }

  deleteAiDecalElement(containerId: string, objectGuid: string): Promise<AiDecalElementResult> {
    const url = `${this.environment.apiHost}/project/container/${containerId}/design/decal/${objectGuid}`;

    return new Promise<AiDecalElementResult>((resolve, reject) => {
      this.httpClient.delete<AiDecalElementResult>(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            const message = this.extractAiDecalErrorMessage(error.error);
            if (message) {
              reject(new AiDecalError(message));
              return;
            }
            if (error.status >= 400 && error.status < 500) {
              reject(new AiDecalError('The request was rejected.'));
              return;
            }
          }
          reject(error);
        }
      );
    });
  }

  // The project service can serialize a caught exception as { "<exception message>": { message, ... } };
  // the useful text is nested one level under a key we don't know ahead of time.
  private extractAiDecalErrorMessage(body: any): string {
    if (typeof body === 'string') {
      return body;
    }
    if (!body || typeof body !== 'object') {
      return '';
    }
    if (typeof body.message === 'string' && body.message) {
      return body.message;
    }
    if (typeof body.error === 'string' && body.error) {
      return body.error;
    }
    const keys = Object.keys(body);
    if (keys.length === 1) {
      const nested = body[keys[0]];
      if (nested && typeof nested === 'object' && typeof nested.message === 'string' && nested.message) {
        return nested.message;
      }
    }
    return '';
  }
}
